import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Mail, Loader2, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function AdminLogin() {
  const { login, admin } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (admin) navigate("/admin", { replace: true });
  }, [admin, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) return toast.error("Enter email and password");
    setSubmitting(true);
    const res = await login(email, password);
    setSubmitting(false);
    if (res.ok) {
      toast.success("Welcome back");
      navigate("/admin", { replace: true });
    } else {
      toast.error(res.error || "Login failed");
    }
  };

  return (
    <div className="min-h-[100dvh] flex items-center justify-center p-6 dot-grid" data-testid="admin-login-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground mb-6 transition"
          data-testid="back-to-site"
        >
          <ArrowLeft className="h-4 w-4" /> Back to site
        </Link>

        <div className="bg-white rounded-3xl border border-border p-8 shadow-xl">
          <div className="mb-6">
            <div className="inline-flex items-center justify-center h-12 w-12 rounded-2xl bg-[#FF4D4D] text-white mb-4">
              <Lock className="h-5 w-5" />
            </div>
            <h1 className="text-3xl font-black tracking-tight">Admin Sign In</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Manage frames and view submissions.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <label className="text-sm font-semibold mb-2 block">Email</label>
              <div className="relative">
                <Mail className="h-4 w-4 absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@wishes.com"
                  className="h-12 rounded-2xl pl-11 focus-visible:ring-[#FF4D4D]"
                  data-testid="email-input"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-semibold mb-2 block">Password</label>
              <div className="relative">
                <Lock className="h-4 w-4 absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="h-12 rounded-2xl pl-11 focus-visible:ring-[#FF4D4D]"
                  data-testid="password-input"
                />
              </div>
            </div>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="login-submit-btn"
              className="w-full h-12 rounded-full bg-[#FF4D4D] hover:bg-[#FF7373] text-white font-bold"
            >
              {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : "Sign in"}
            </Button>
          </form>

          <p className="text-xs text-muted-foreground mt-6 text-center">
            Default: admin@wishes.com / admin123
          </p>
        </div>
      </motion.div>
    </div>
  );
}
